/**
 * Created by neo on 01.04.23.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { AuthLayout } from '../../../Components/Auth/AuthLayout/AuthLayout';
import { ErrorAlert } from '../../../Components/Auth/Alerts/ErrorAlert';
import { WellnessMessages } from '../../../Components/Auth/WellnessMessages';
import { Button } from '../../../Components/Button';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useAuth0 } from '@auth0/auth0-react';
import { Container, Form, FormGroup } from 'reactstrap';
import { useSearchParams } from 'react-router-dom';
import { FormLabel } from '../../../Components/Form/FormLabel';
import { Input } from '../../../Components/Input';
import { EMAIL_REGEX } from '../../../Utils/Constants';
import Backend from '../../../Services/Http/Backend';
import { useRootStore } from '../../../Store/useRootStore';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const FormContainer = styled(Container)`
  padding: 0 1rem 0 1rem;
  animation: ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.3s both;
`;

const StyledFormGroup = styled(FormGroup)`
  margin-bottom: 0;
`;

const StyledFormLabel = styled(FormLabel)`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
`;

const InputWrapper = styled.div`
  position: relative;
  
  &::before {
    content: '✉';
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 18px;
    z-index: 1;
    opacity: 0.6;
  }
`;

const StyledInput = styled(Input)`
  background: rgba(255, 255, 255, 0.1) !important;
  border: 2px solid rgba(255, 255, 255, 0.15) !important;
  border-radius: 12px !important;
  padding: 16px 16px 16px 48px !important;
  font-size: 1rem !important;
  color: white !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4) !important;
  }
  
  &:focus {
    background: rgba(255, 255, 255, 0.15) !important;
    border-color: #00dda2 !important;
    box-shadow: 0 0 0 4px rgba(36, 220, 164, 0.15) !important;
    outline: none !important;
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.05) !important;
    opacity: 0.7;
  }
`;

const ButtonContainer = styled.div`
  padding-top: 1.5rem;
  animation: ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.4s both;
`;

const StyledButton = styled(Button)`
  background: linear-gradient(135deg, #00dda2 0%, #007763 100%) !important;
  /* Hero CTA sits on a gradient that darkens to #007763 — the shared button's
     near-black text falls below contrast there, so this screen keeps white. */
  color: #ffffff !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 16px 24px !important;
  font-size: 0.95rem !important;
  font-weight: 700 !important;
  letter-spacing: 1px !important;
  text-transform: uppercase !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 4px 16px rgba(36, 220, 164, 0.3) !important;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(36, 220, 164, 0.4) !important;
  }
  
  &:active:not(:disabled) {
    transform: translateY(0) !important;
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.2) !important;
    box-shadow: none !important;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 12px !important;
  padding: 14px 24px !important;
  font-size: 0.9rem !important;
  font-weight: 600 !important;
  color: white !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  animation: ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.35s both;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
`;

const DividerText = styled.span`
  padding: 0 16px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export type LoginScreenProps = {};

export const LoginScreen: React.FC<LoginScreenProps> = observer((props) => {
  const { authentication } = useRootStore();
  const [searchParams] = useSearchParams();

  const { t } = useTrans();
  const { loginWithRedirect, logout, isLoading, isAuthenticated, user, getAccessTokenSilently } = useAuth0();

  // Get returnTo from URL params (passed from Root.tsx when redirecting to /auth)
  const returnTo = searchParams.get('returnTo') || '/home';

  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [email, setEmail] = useState('');
  const [processing, setProcessing] = useState(false);

  const emailValid = email.length > 0 && EMAIL_REGEX.test(email);
  const buttonDisabled = isLoading || processing || !emailValid;
  const secondaryButtonDisabled = isLoading || processing;

  const authenticateButtonText = isAuthenticated
    ? t('newLoginScreen.continueButton', { defaultValue: 'Continue to App' }).toUpperCase()
    : t('newLoginScreen.authenticateButton', { defaultValue: 'Continue' }).toUpperCase();

  const subtitleText = isAuthenticated
    ? t('newLoginScreen.auth.subtitleLoggedIn', {
        defaultValue:
          'You are currently logged in as {{userName}}. Click on Continue to continue to the app or Logout to login with another user.',
        userName: user?.email,
      })
    : t('newLoginScreen.auth.subtitle', {
        defaultValue:
          '**Neu hier?**\n' +
          'Gib deine geschäftliche oder die bei deiner Firma hinterlegte E-Mail-Adresse ein,  \n' +
          'um dich zu registrieren.\n' +
          '\n' +
          '**Willkommen zurück!**\n' +
          'Du hast bereits ein Konto? Melde dich mit deiner Account-E-Mail-Adresse an.',
      });

  // Handle post-login token refresh when user returns from Auth0
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    const handlePostLogin = async () => {
      try {
        setProcessing(true);
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: 'https://api.kinastic.com',
            scope: 'offline_access openid profile email',
            redirect_uri: window.location.origin + '/auth',
          },
        });
        if (!cancelled) {
          await authentication.storeAccessToken(token);
        }
      } catch (err) {
        console.error('AuthLoginScreen - failed to refresh token after Auth0 redirect:', err);
      } finally {
        if (!cancelled) {
          setProcessing(false);
        }
      }
    };

    handlePostLogin();
    return () => { cancelled = true; };
  }, [isAuthenticated, getAccessTokenSilently, authentication]);

  const handleChangeEmail = React.useCallback(({ target: { value } }) => {
    setEmail(value);
  }, []);

  const handleShowErrorAlert = React.useCallback((error?: string) => {
    setErrorCode(error);
    setShowErrorAlert(true);
  }, []);

  const handleCloseAlert = React.useCallback(() => {
    setShowErrorAlert(false);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (emailValid) {
      setProcessing(true);
      new Backend()
        .post('/uaa/authentication/request-strategy', { identifier: email })
        .then((response) =>
          loginWithRedirect({
            appState: { returnTo },
            authorizationParams: {
              audience: 'https://api.kinastic.com', // the audience is needed to get a proper access_token
              scope: 'offline_access openid profile email', // offline_access is needed for refresh token
              redirect_uri: window.location.origin + '/auth',
              prompt: 'login',
              connection: response.connection ?? undefined, // seems stupid but has to be like this (since the value can be null and JS has issues with that)
              ...response.additionalParameters,
            },
          }),
        )
        .catch(() =>
          // request-strategy legitimately 401s on strategy errors — fall back to the default Auth0 flow
          loginWithRedirect({
            appState: { returnTo },
            authorizationParams: {
              audience: 'https://api.kinastic.com',
              scope: 'offline_access openid profile email',
              redirect_uri: window.location.origin + '/auth',
              prompt: 'login',
            },
          }),
        )
        .finally(() => setProcessing(false));
    }
  };

  const handleLogout = React.useCallback(() => {
    logout({
      logoutParams: {
        returnTo: `https://coach.kinastic.com/auth`,
      },
    });
  }, [logout]);

  // useEffect(() => {
  //   if (!isAuthenticated) {
  //     loginWithRedirect({
  //       authorizationParams: {
  //         audience: 'https://api.kinastic.com', // the audience is needed to get a proper access_token
  //         scope: 'offline_access openid profile email', // offline_access is needed for refresh token
  //         redirect_uri: window.location.origin + '/auth',
  //       },
  //     });
  //   }
  // }, [loginWithRedirect, isAuthenticated]);

  return (
    <AuthLayout
      title={t('newLoginScreen.auth0.title', { defaultValue: 'Welcome to KINASTIC' })}
      subtitle={subtitleText}
      overlayRightTitle={t('newLoginScreen.introTitle', { defaultValue: 'Hello\n' + 'New\n' + 'You' })}
      image={require('../../../Assets/Images/auth_4.png')}
      footer={<WellnessMessages messageCount={5} autoRotate={true} rotationInterval={6000} />}
    >
      <Form onSubmit={handleLogin}>
        <FormContainer>
          <StyledFormGroup>
            <StyledFormLabel>{t('newLoginScreen.emailLabel', { defaultValue: 'Your e-mail address' })}</StyledFormLabel>
            <InputWrapper>
              <StyledInput
                placeholder={t('newLoginScreen.emailPlaceholder', { defaultValue: 'you@company.com' })}
                onChange={handleChangeEmail}
                value={email}
                disabled={processing || isLoading}
                type="email"
                autoComplete="email"
              />
            </InputWrapper>
          </StyledFormGroup>
        </FormContainer>
        <ButtonContainer>
          <StyledButton block={true} disabled={buttonDisabled} onClick={handleLogin}>
            {authenticateButtonText}
          </StyledButton>
        </ButtonContainer>
        {isAuthenticated && (
          <>
            <Divider>
              <DividerText>{t('newLoginScreen.or', { defaultValue: 'or' })}</DividerText>
            </Divider>
            <ButtonContainer style={{ paddingTop: 0 }}>
              <SecondaryButton block={true} disabled={secondaryButtonDisabled} onClick={handleLogout}>
                {t('newLoginScreen.logout', { defaultValue: 'Logout' })}
              </SecondaryButton>
            </ButtonContainer>
          </>
        )}
        <ErrorAlert
          toggle={handleCloseAlert}
          isOpen={showErrorAlert}
          title={t(`errors.${errorCode}`, { defaultValue: errorCode })}
          onDismiss={handleCloseAlert}
        />
      </Form>
    </AuthLayout>
  );
});
